import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { IMPROVE_LANGUAGES, IMPROVE_OPTIONS } from './improveHelpers';
import AIPreviewModal from './AIPreviewModal';
import './aiImprove.css';

/**
 * Right-side glass panel: Improve your writing / Improve Selection.
 * Options trigger streaming; results appear in the embedded preview.
 * Portaled to document.body so compose overflow/stacking never clips it.
 */
export default function AIWritingPanel({
  open,
  selectionMode = false,
  onClose,
  onRunAction,
  preview,
}) {
  const [translateOpen, setTranslateOpen] = useState(false);
  const [language, setLanguage] = useState('English');

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const title = selectionMode ? '✨ Improve Selection' : '✨ Improve your writing';

  const handleOption = (option) => {
    if (option.hasLanguages) {
      setTranslateOpen((v) => !v);
      return;
    }
    onRunAction?.(option.id);
  };

  return createPortal(
    <div className="aii-panel-root" role="complementary" aria-label="Improve with AI">
      <div className="aii-panel-backdrop" onClick={onClose} aria-hidden="true" />
      <aside className="aii-panel">
        <header className="aii-panel-header">
          <div>
            <h2>{title}</h2>
            <p>Choose how AI should refine your draft. Nothing is sent or overwritten until you confirm.</p>
          </div>
          <button type="button" className="aii-panel-close" onClick={onClose} aria-label="Close">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
            </svg>
          </button>
        </header>

        <div className="aii-panel-body">
          <div className="aii-option-list">
            {IMPROVE_OPTIONS.map((option) => (
              <div key={option.id} className="aii-option-block">
                <button
                  type="button"
                  className="aii-option"
                  onClick={() => handleOption(option)}
                  disabled={preview?.streaming || preview?.loading}
                >
                  <span className="aii-option-icon" aria-hidden="true">{option.icon}</span>
                  <span className="aii-option-copy">
                    <strong>{option.label}</strong>
                    <span>{option.description}</span>
                    {option.bullets?.length > 0 && (
                      <ul>
                        {option.bullets.map((b) => (
                          <li key={b}>{b}</li>
                        ))}
                      </ul>
                    )}
                  </span>
                </button>

                {option.hasLanguages && translateOpen && (
                  <div className="aii-translate-box">
                    <label htmlFor="aii-translate-lang">Translate into</label>
                    <select
                      id="aii-translate-lang"
                      value={language}
                      onChange={(e) => setLanguage(e.target.value)}
                    >
                      {IMPROVE_LANGUAGES.map((lang) => (
                        <option key={lang} value={lang}>{lang}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="aii-btn aii-btn-primary aii-btn-block"
                      onClick={() => onRunAction?.('translate', { targetLanguage: language })}
                      disabled={preview?.streaming || preview?.loading}
                    >
                      Translate to {language}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>

          <AIPreviewModal
            open={Boolean(preview?.open)}
            original={preview?.original}
            aiText={preview?.aiText}
            streaming={preview?.streaming}
            loading={preview?.loading}
            error={preview?.error}
            onReplace={preview?.onReplace}
            onAppend={preview?.onAppend}
            onCopy={preview?.onCopy}
            onRegenerate={preview?.onRegenerate}
            onUndo={preview?.onUndo}
            onCancel={preview?.onCancel}
            canUndo={preview?.canUndo}
          />
        </div>
      </aside>
    </div>,
    document.body
  );
}
